import type { AgentSearchResult } from '~~/core/entities/AgentSearch';
import type { AgentsRepository } from '~~/core/ports/repositories/AgentsRepository';

import { useLogger } from '~/composables/useLogger';
import { isAgentNotFoundError } from '~~/core/entities/agentNotFound';

const logger = useLogger('selectedAgentSummary');

export type SelectedAgentSummaryResult =
	| { status: 'found'; slug: string; agent: AgentSearchResult }
	| { status: 'not-found' | 'unavailable'; slug: string }
	| null;

interface LoadSelectedAgentSummaryOptions {
	isEnabled: boolean;
	agentSlug?: string;
	repository: Pick<AgentsRepository, 'getOne'>;
	signal?: AbortSignal;
}

export async function loadSelectedAgentSummary({
	isEnabled,
	agentSlug,
	repository,
	signal
}: LoadSelectedAgentSummaryOptions): Promise<SelectedAgentSummaryResult> {
	const normalizedAgentSlug = isEnabled ? agentSlug?.trim() : '';

	if (!normalizedAgentSlug || signal?.aborted) return null;

	const [agent, error] = await repository.getOne(normalizedAgentSlug, signal);
	// A superseded lookup must not change the current selection or report an error.
	if (signal?.aborted) return null;

	if (error) {
		if (isAgentNotFoundError(error)) {
			return { status: 'not-found', slug: normalizedAgentSlug };
		}

		logger.warn('Failed to load selected agent summary', {
			agent_slug: normalizedAgentSlug,
			error
		});
		return { status: 'unavailable', slug: normalizedAgentSlug };
	}

	if (!agent || agent.slug !== normalizedAgentSlug) {
		logger.warn('Selected agent summary does not match the requested agent', {
			agent_slug: normalizedAgentSlug
		});
		return { status: 'unavailable', slug: normalizedAgentSlug };
	}

	return { status: 'found', slug: normalizedAgentSlug, agent };
}
